import { useToday } from "@mapcraft-tasks/client-store/react";
import { addDays, type PlainDate } from "@mapcraft-tasks/domain";
import { useNavigate } from "@tanstack/react-router";

/**
 * Листалка по соседним дням — та же, что у Tana рядом с датой: «‹ › сегодня».
 *
 * Компонент один на оба места, где в системе выбирается день: заголовок страницы дня
 * и панель сроков справа. Шаг «день ← → день» — самое частое движение по календарю,
 * и держать для него два разных виджета значило бы разъехаться в поведении.
 *
 * Дату не хранит: и страница (адрес), и панель (свой `useState`) владеют ею сами —
 * листалка только просит следующую.
 */
export function DayNav({
	date,
	onDate,
	picker = false,
	className = ""
}: {
	date: PlainDate;
	onDate: (date: PlainDate) => void;
	/** Показать дату средним сегментом — она же и поле выбора для дальнего прыжка. */
	picker?: boolean;
	className?: string;
}) {
	const today = useToday();
	return (
		<span className={`day-nav ${className}`}>
			<button
				type="button"
				className="day-nav-prev"
				title="предыдущий день"
				aria-label="предыдущий день"
				onClick={() => onDate(addDays(date, -1))}
			>
				<Arrow back />
			</button>
			{/*
			 * Дата внутри группы, а не полем рядом: в 288px правой колонки «‹ › сегодня»
			 * и отдельное поле вместе не помещаются, и шапка панели ломалась на две строки
			 * ровно в тот момент, когда с сегодня уходили. Своё поле группа кормит сама.
			 */}
			{picker && (
				<input
					type="date"
					value={date}
					title="выбрать дату"
					// Клик по дате открывает календарь целиком, а не ставит каретку в одну
					// из трёх её частей: без значка-календаря сегмент и есть кнопка выбора.
					onClick={(e) => e.currentTarget.showPicker?.()}
					onChange={(e) => {
						// Пустое значение — это очистка поля посреди набора, а не «нет даты»:
						// у панели дата обязана быть всегда.
						if (e.target.value !== "") {
							onDate(e.target.value);
						}
					}}
					className="tabular"
				/>
			)}
			<button
				type="button"
				className="day-nav-next"
				title="следующий день"
				aria-label="следующий день"
				onClick={() => onDate(addDays(date, 1))}
			>
				<Arrow />
			</button>
			{/* «Сегодня» — третий сегмент той же группы, и появляется он, только когда с
			    сегодня ушли: на самом сегодня это кнопка в никуда. */}
			{date !== today && (
				<button type="button" className="day-nav-today" onClick={() => onDate(today)}>
					сегодня
				</button>
			)}
		</span>
	);
}

/** Тот же контурный шеврон, что у строк аутлайна: сетка 24, штрих 2, показ в 14px. */
function Arrow({ back = false }: { back?: boolean }) {
	return (
		<svg
			viewBox="0 0 24 24"
			width={14}
			height={14}
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={{ transform: back ? "rotate(180deg)" : undefined }}
		>
			<path d="M9 6l6 6-6 6" />
		</svg>
	);
}

/**
 * Переход на страницу дня.
 *
 * Сегодняшний день живёт по `/today`, а не по `/day/<сегодня>`: иначе на одну и ту же
 * страницу вели бы два адреса, и «сегодня» в сайдбаре подсвечивалось бы не всегда.
 */
export function useGoToDay(): (date: PlainDate) => void {
	const navigate = useNavigate();
	const today = useToday();
	return (date) => {
		void (date === today ? navigate({ to: "/today" }) : navigate({ to: "/day/$date", params: { date } }));
	};
}

/**
 * Заголовок страницы дня. Соседние дни человек называет словами, а не датой, — поэтому
 * «Вчера»/«Сегодня»/«Завтра»; дальше слова кончаются и остаётся дата прописью.
 * Сам ISO всегда стоит подзаголовком рядом, так что однозначность не теряется.
 */
export function dayTitle(date: PlainDate, today: PlainDate): string {
	if (date === today) {
		return "Сегодня";
	}
	if (date === addDays(today, -1)) {
		return "Вчера";
	}
	if (date === addDays(today, 1)) {
		return "Завтра";
	}
	const text = new Intl.DateTimeFormat("ru", {
		day: "numeric",
		month: "long",
		// Год — только чужой: в своём он шум, в соседнем без него не разобраться.
		year: date.slice(0, 4) === today.slice(0, 4) ? undefined : "numeric",
		timeZone: "UTC"
	}).format(new Date(`${date}T00:00:00Z`));
	return text.charAt(0).toUpperCase() + text.slice(1);
}
